import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'

import { HBox, Box } from 'app/views/core/Box'

import LessonDotnav from './LessonDotnav'
import HintSelectionModal from './HintSelectionModal'
import HintModal from './HintModal'
import ContentCompletionStatus from './ContentCompletionStatus'

import { connect } from 'react-redux'
import * as progress from 'app/state/modules/progress'

const NavButton = ({ label, kind, active, onClick }) => (
  <button
    className={`uk-button uk-button-small${kind ? ` uk-button-${kind}` : ''}`}
    disabled={!active}
    style={{
      width: '100%',
      height: '100%',
      borderRadius: 10,
    }}
    onClick={onClick}
  >
    {label}
  </button>
)

/**
 * Displays the step-by-step text of the lesson, an optional Product Tour button, and a button to
 * view hints, if available for any given step.
 */
function LessonInstructions(props) {
  const { hasTests, contentCompletedAt, contentPassedAt, title, steps } = props
  const { isReadyToFinish, shouldOfferProductTour, onProductTourRequested, onAttemptCompleteLesson } = props
  const [stepIndex, setStepIndex] = useState(0)
  const canNavigateBack = steps.length > 0 && stepIndex > 0
  const canNavigateForward = steps.length > 0 && stepIndex < steps.length - 1
  const step = steps[stepIndex]

  const hints = step.hints.sort((a, b) => a.sort_order - b.sort_order)
  const [wantsHint, setWantsHint] = useState(false)
  const [selectedHintIndex, setSelectedHintIndex] = useState(-1)
  const onShowHintClick = () => {
    if (hints.length === 1) {
      setSelectedHintIndex(0)
    } else {
      setWantsHint(true)
    }
  }
  const onCloseHintModal = () => {
    setSelectedHintIndex(-1)
    if (hints.length !== 1) {
      setWantsHint(true)
    }
  }

  const { scrollToTop, trackProgress } = props
  useEffect(() => {
    scrollToTop()
    trackProgress(stepIndex)
  }, [stepIndex])

  return (
    <>
      <LessonDotnav
        currentStepIndex={stepIndex}
        numSteps={steps.length}
        onSelectStep={setStepIndex}
      />
      {selectedHintIndex >= 0 ? (
        <HintModal
          title={hints[selectedHintIndex].title}
          markdown={hints[selectedHintIndex].markdown}
          onClose={onCloseHintModal}
        />
      ) : (
        wantsHint && selectedHintIndex < 0 && (
          <HintSelectionModal
            hints={hints}
            onSelectHint={(index) => setSelectedHintIndex(index)}
            onClose={() => setWantsHint(false)}
          />
        )
      )}
      {stepIndex === 0 && shouldOfferProductTour && (
        <button
          className="uk-button uk-button-primary"
          style={{ margin: 20, marginBottom: 0 }}
          onClick={onProductTourRequested}
        >
          Product Tour
        </button>
      )}
      <div style={{ paddingLeft: 10, marginTop: 22 }}>
        <ReactMarkdown rehypePlugins={[rehypeRaw]} linkTarget="_blank">
          {step.markdown}
        </ReactMarkdown>
      </div>
      {hints.length > 0 && (
        <button
          className="uk-button"
          style={{ marginBottom: 20 }}
          onClick={onShowHintClick}
        >
          Show Hint
        </button>
      )}
      {!canNavigateForward && (
        <ContentCompletionStatus
          hasTests={hasTests}
          completedAt={contentCompletedAt}
          passedAt={contentPassedAt}
        />
      )}
      <HBox>
        <Box>
          <NavButton
            label="Previous"
            kind="secondary"
            active={canNavigateBack}
            onClick={() => setStepIndex((prev) => prev - 1)}
          />
        </Box>
        <Box grow />
        <Box>
          {canNavigateForward ? (
            <NavButton
              label="Next"
              kind="primary"
              active={true}
              onClick={() => setStepIndex((prev) => prev + 1)}
            />
          ) : (
            <NavButton
              label="Finish"
              kind={isReadyToFinish ? 'primary' : ''}
              active={true}
              onClick={onAttemptCompleteLesson}
            />
          )}
        </Box>
      </HBox>
    </>
  )
}
LessonInstructions.propTypes = {
  hasTests: PropTypes.bool.isRequired,
  contentCompletedAt: PropTypes.string,
  contentPassedAt: PropTypes.string,
  title: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  shouldOfferProductTour: PropTypes.bool.isRequired,
  scrollToTop: PropTypes.func.isRequired,
  onProductTourRequested: PropTypes.func.isRequired,
  onAttemptCompleteLesson: PropTypes.func.isRequired,
  isReadyToFinish: PropTypes.bool.isRequired,
  trackProgress: PropTypes.func.isRequired,
}

export default connect((state, ownProps) => ({
  isReadyToFinish: !ownProps.hasTests || !state.hacker.mustPassCodingExercise || state.hacker.currentCodeSubmissions.some((x) => x.test.passed),
}), (dispatch) => ({
  trackProgress: (stepIndex) => dispatch(progress.track(stepIndex)),
}))(LessonInstructions)
